/**
 * Valida o DNA da máquina no backend proxy.
 * Lança um erro se a validação falhar ou se houver problemas na comunicação.
 */
export async function validateDNA(dna: string, token: string): Promise<boolean> {
  // 1. Validação de Entrada
  if (!dna || typeof dna !== 'string' || dna.trim() === '') {
    throw new Error("DNA inválido ou vazio fornecido para validação.");
  }
  if (!token || typeof token !== 'string' || token.trim() === '') {
    throw new Error("Token de autenticação inválido ou vazio fornecido para validação de DNA.");
  }

  try {
    const res = await fetch('/api/machine/dna', {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ dna: dna }),
    });

    let jsonResponse: any = {};
    // 2. Tratamento Robusto de JSON
    try {
      jsonResponse = await res.json();
    } catch (parseError) {
      console.error("[validateDNA] Erro ao parsear JSON da resposta:", parseError);
      // Se não conseguiu parsear o JSON, mas a resposta não foi OK, o erro será tratado abaixo.
      // Se a resposta foi OK mas o JSON é inválido, lançamos um erro específico.
      if (res.ok) {
        throw new Error("Resposta inválida do servidor: esperado JSON, mas recebeu outro formato ao validar DNA.");
      }
      // Se não foi OK, o erro será capturado no `if (!res.ok)` abaixo.
    }

    // 3. Tratamento de Respostas Não-OK (HTTP status codes 4xx, 5xx)
    if (!res.ok) {
      const detailMessage = jsonResponse?.detail || jsonResponse?.message || res.statusText;
      const statusCode = res.status;

      // Mensagens de erro mais específicas para o chamador
      if (statusCode === 401 || statusCode === 403) {
        throw new Error(`Acesso não autorizado para o DNA (${statusCode}): ${detailMessage}`);
      } else if (statusCode === 400) {
        throw new Error(`Requisição inválida para validação de DNA (${statusCode}): ${detailMessage}`);
      } else {
        throw new Error(`Erro no servidor ao validar DNA (${statusCode}): ${detailMessage}`);
      }
    }

    // 4. Verificação da Validade do DNA na Resposta
    // A propriedade esperada é "machine-status" e seu valor deve ser `true`.
    const isValid = jsonResponse && jsonResponse["machine-status"] === true;

    if (!isValid) {
      // O backend respondeu 200 OK, mas o status da máquina não é válido/autorizado.
      console.warn("[validateDNA] DNA não autorizado na resposta do backend:", jsonResponse);
      throw new Error("Máquina não autorizada ou DNA inválido de acordo com o servidor.");
    }

    console.log("[validateDNA] Resposta da validação com sucesso:", jsonResponse);
    return true; // Retorna true se tudo estiver OK
  } catch (err: any) {
    // 5. Captura de Erros de Rede ou Lançados Internamente
    console.error("[validateDNA] Erro inesperado na validação do DNA:", err);
    // Relança o erro para que o componente chamador (ex: LoginPage) possa lidar com ele
    // e exibir um toast ou outra mensagem ao usuário.
    throw err;
  }
}